import { OverlayContainer } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { IconComponent } from '@shared/ui-shared';
import { startWith } from 'rxjs';
import { IconsPathProvider } from '../../providers/icons-path.provider';

@Component({
  selector: 'csl-datepicker-header',
  standalone: true,
  imports: [CommonModule, IconComponent],
  templateUrl: './datepicker-header.component.html',
  styleUrls: ['./datepicker-header.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [IconsPathProvider],
})
export class DatepickerHeaderComponent<D> {
  private _calendar = inject<MatCalendar<D>>(MatCalendar);
  private _dateAdapter = inject<DateAdapter<D>>(DateAdapter);
  private _dateFormats = inject(MAT_DATE_FORMATS);
  private _destroyRef = inject(DestroyRef);
  private _overlayContainer = inject(OverlayContainer);

  private readonly _yearsPerPage = 24;

  readonly periodLabel = signal('');

  readonly prevButtonLabel = signal('');
  readonly nextButtonLabel = signal('');

  constructor() {
    this._calendar.stateChanges.pipe(startWith(null), takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.periodLabel.set(
        this._dateAdapter.format(this._calendar.activeDate, this._dateFormats.display.monthYearLabel),
      );
      this._updateLabels();
      setTimeout(() => this.fixGridcellRolesForNVDAScreenReader(), 0);
    });
  }

  private _updateLabels(): void {
    const view = this._calendar.currentView;
    if (view === 'month') {
      this.prevButtonLabel.set('Previous Month');
      this.nextButtonLabel.set('Next Month');
    } else if (view === 'year') {
      this.prevButtonLabel.set('Previous Year');
      this.nextButtonLabel.set('Next Year');
    } else {
      this.prevButtonLabel.set('Previous 24 Years');
      this.nextButtonLabel.set('Next 24 Years');
    }
  }

  /** Handles user clicks on the previous button. */
  previousClicked(): void {
    this._calendar.activeDate =
      this._calendar.currentView == 'month'
        ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, -1)
        : this._dateAdapter.addCalendarYears(
            this._calendar.activeDate,
            this._calendar.currentView == 'year' ? -1 : -this._yearsPerPage,
          );
  }

  /** Handles user clicks on the next button. */
  nextClicked(): void {
    this._calendar.activeDate =
      this._calendar.currentView == 'month'
        ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, 1)
        : this._dateAdapter.addCalendarYears(
            this._calendar.activeDate,
            this._calendar.currentView == 'year' ? 1 : this._yearsPerPage,
          );
  }

  currentPeriodClicked(): void {
    this._calendar.currentView = this._calendar.currentView == 'month' ? 'multi-year' : 'month';
  }

  previousEnabled(): boolean {
    if (!this._calendar.minDate) {
      return true;
    }
    return !this._calendar.minDate || !this._isSameView(this._calendar.activeDate, this._calendar.minDate);
  }

  /** Whether the next period button is enabled. */
  nextEnabled(): boolean {
    return !this._calendar.maxDate || !this._isSameView(this._calendar.activeDate, this._calendar.maxDate);
  }

  /** Whether the two dates represent the same view in the current view mode (month or year). */
  private _isSameView(date1: D, date2: D): boolean {
    if (this._calendar.currentView == 'month') {
      return (
        this._dateAdapter.getYear(date1) == this._dateAdapter.getYear(date2) &&
        this._dateAdapter.getMonth(date1) == this._dateAdapter.getMonth(date2)
      );
    }
    if (this._calendar.currentView == 'year') {
      return this._dateAdapter.getYear(date1) == this._dateAdapter.getYear(date2);
    }
    // Otherwise we are in 'multi-year' view.
    return this.isSameMultiYearView(this._dateAdapter, date1, date2, this._calendar.minDate, this._calendar.maxDate);
  }

  private isSameMultiYearView<D>(
    dateAdapter: DateAdapter<D>,
    date1: D,
    date2: D,
    minDate: D | null,
    maxDate: D | null,
  ): boolean {
    const year1 = dateAdapter.getYear(date1);
    const year2 = dateAdapter.getYear(date2);
    const startingYear = this.getStartingYear(dateAdapter, minDate, maxDate);
    return (
      Math.floor((year1 - startingYear) / this._yearsPerPage) ===
      Math.floor((year2 - startingYear) / this._yearsPerPage)
    );
  }

  private getStartingYear<D>(dateAdapter: DateAdapter<D>, minDate: D | null, maxDate: D | null): number {
    let startingYear = 0;
    if (maxDate) {
      const maxYear = dateAdapter.getYear(maxDate);
      startingYear = maxYear - this._yearsPerPage + 1;
    } else if (minDate) {
      startingYear = dateAdapter.getYear(minDate);
    }
    return startingYear;
  }

  private fixGridcellRolesForNVDAScreenReader(): void {
    // Move role="gridcell" from the <td> onto the focusable <button> so NVDA sees the
    // focused node itself as a gridcell and switches to focus mode.
    const overlayContainer = this._overlayContainer.getContainerElement();
    overlayContainer.querySelectorAll<HTMLElement>('.mat-datepicker-content td[role="gridcell"]').forEach((cell) => {
      const button = cell.querySelector<HTMLElement>('button');
      if (!button) {
        return;
      }
      cell.removeAttribute('role');
      button.setAttribute('role', 'gridcell');
    });
  }
}
